import {put, call} from 'redux-saga/effects';
import Docket from "../../entities/docket";
import * as Actions from "../actions/docketActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type DocketAction = {
    docketId: string;
    docket: Docket;
    dockets: Docket[];
};

export function* fetchOneDocket(action: DocketAction) {
	try {
		const docket: Docket = yield call(api.findOne, 'docket', action.docketId);

		// Now we can update the user interface
		yield put({type: Actions.DOCKET_FETCH_ONE_SUCCESS, docketId: action.docketId, docket});
	}
	catch(err) {
		yield put({type: Actions.DOCKET_FETCH_ONE_FAILURE, docketId: action.docketId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {DocketAction} action
 */
export function* saveDocket(action: DocketAction) {
    // console.debug('⮂ DOCKET_SAVE saga', action.docket?.id);
    
	if (action.docket === undefined || action.docket === null) {
		console.error('saveDocket received a null');
		throw new Error('saveDocket received a null');
	}

	if (!(action.docket instanceof Docket)) {
		action.docket = new Docket(action.docket);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'docket', action.docket);
		if (addResult.isErr()) {
			console.error(`⮂ saveDocket: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.DOCKET_SAVE_FAILURE, docket: action.docket, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DOCKET_SAVE, docket: action.docket});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'docket', action.docket.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.DOCKET_SAVE_FAILURE, docket: action.docket, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {DocketAction} action
 * @param {Docket[]} action.dockets
 */
export function* saveManyDockets(action: DocketAction) {
    // console.debug('⮂ DOCKET_SAVE_MANY saga', action.dockets.map(docket => docket.id).join(','));
    
    if (!Array.isArray(action.dockets)) {
        console.error('saveManyDockets expected an array, received', action.dockets);
        throw new Error('saveManyDockets expected an array');
    }

	try {
		const toSave: Docket[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.dockets.length; i++) {
			const docket = action.dockets[i];
			docket.persisted = false;
			toSave.push(docket);
		}

		const addResult = yield call(idb.add, 'docket', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyDockets: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.DOCKET_SAVE_MANY_FAILURE, dockets: action.dockets, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DOCKET_SAVE_MANY, dockets: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.DOCKET_SAVE_MANY_FAILURE, dockets: action.dockets, status: 500, err});

		console.error(`Error saving ${action.dockets.length} dockets to database`, err);
	}
}

/**
 * @param {DocketAction} action
 */
export function* removeDocket(action: DocketAction) {
    const userId = getCurrentUserId();
	if (!userId || action.docket === undefined || action.docket === null || !(action.docket instanceof Docket)) {
			console.error('removeDocket expected type Docket, received', action.docket);
			throw new Error('removeDocket expected type Docket');
		}

	try {
		// Mark as deleted
		action.docket.status = 'D';
		action.docket.addedOn = newHRTimestamp();
		action.docket.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'docket', action.docket);
		if (addResult.isErr()) {
			console.error(`⮂ removeDocket: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.DOCKET_REMOVE_FAILURE, docket: action.docket, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DOCKET_REMOVE, docket: action.docket});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.DOCKET_REMOVE_FAILURE, docket: action.docket, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Docket.
	*
	* @param {DocketAction} action
	*/
export function* restoreDocket(action: DocketAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreDocket expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.docket === undefined || action.docket === null) {
        console.error('restoreDocket expected a Docket, received null');
        throw new Error('restoreDocket expected a Docket, received null');
    }
    else if (!(action.docket instanceof Docket)) action.docket = new Docket(action.docket); 
    
	try {
		// Mark as restored
		action.docket.status = 'U';
		action.docket.addedOn = newHRTimestamp();
		action.docket.addedBy = userId;
		action.docket.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'docket', action.docket);
		if (addResult.isErr()) {
			console.error(`⮂ restoreDocket: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.DOCKET_RESTORE_FAILURE, docket: action.docket, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DOCKET_RESTORE, docket: action.docket});
	}
	catch(err) {
		yield put({type: Actions.DOCKET_RESTORE_FAILURE, docket: action.docket, status: 500, err});

		console.error('Error restoring from database', err);
	}
}